/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Icon } from '@components/atoms'
import { DrawerSubMenuList } from './DrawerSubMenuList'
import { ListItemButton } from '@mui/material'

import { useAppDispatch, useAppSelector, mainActions, MenuType } from '@redux/index'
import { grey } from '@mui/material/colors'
import { useLocation, useNavigate } from 'react-router-dom'

interface PropsType {
	menu: MenuType
	isFirstDepth: boolean
}

export const DrawerMainMenuItem = (props: PropsType) => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const location = useLocation()
	const navigate = useNavigate()

	// redux state
	const openedMenuIdArr: string[] = useAppSelector(state => state.main.openedMenuIdArr)
	const isOpenDrawer: boolean = useAppSelector(state => state.config.isOpenDrawer)

	// variables
	const iconName: string =
		props.menu.icon !== undefined ? props.menu.icon : 'circle-medium'
	const samePathWithSubmenu: MenuType[] = React.useMemo(() => {
		if (props.menu.children !== undefined) {
			return props.menu.children.filter(
				aSubmenu => aSubmenu.to !== undefined && location.pathname === aSubmenu.to,
			)
		} else {
			return []
		}
	}, [props.menu.children, location.pathname])
	const isSubMenuSelected: boolean =
		props.menu.children !== undefined && samePathWithSubmenu.length > 0
	const textColor: string =
		location.pathname === props.menu.to || isSubMenuSelected ? grey[800] : grey[400]
	const isOpened: boolean = openedMenuIdArr.includes(props.menu.id)
	const mainItemIconStyle = React.useMemo(
		() => ({
			color: textColor,
			marginRight: props.isFirstDepth && !isOpenDrawer ? 0 : '0.5rem',
			fontSize:
				props.isFirstDepth && isOpenDrawer
					? '20px'
					: props.isFirstDepth
					? '24px'
					: isOpenDrawer
					? '16px'
					: '20px',
		}),
		[textColor, props.isFirstDepth, isOpenDrawer],
	)

	// state
	const [isOpenSubmenu, setIsOpenSubmenu] = React.useState<boolean>(isOpened)
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

	React.useEffect(() => {
		if (!isOpenDrawer) {
			onCloseSubmenu()
			reduxDispatch(mainActions.OPENED_MENU_ID_ARR([]))
		}
	}, [isOpenDrawer])

	const onOpenSubmenuClick = React.useCallback(
		(event: any) => {
			const newOpenedMenuIdArr: string[] = isOpenDrawer
				? [...openedMenuIdArr]
				: [props.menu.id]
			setAnchorEl(event.currentTarget)

			if (isOpenDrawer) {
				if (openedMenuIdArr.includes(props.menu.id) && isOpenSubmenu) {
					// delete
					const idx = openedMenuIdArr.indexOf(props.menu.id)
					newOpenedMenuIdArr.splice(idx, 1)
				} else if (!openedMenuIdArr.includes(props.menu.id) && !isOpenSubmenu) {
					// add
					newOpenedMenuIdArr.push(props.menu.id)
				}
			}

			reduxDispatch(mainActions.OPENED_MENU_ID_ARR([...newOpenedMenuIdArr]))
			setIsOpenSubmenu(!isOpenSubmenu)
		},
		[isOpenDrawer, openedMenuIdArr, props.menu.id],
	)

	const onNavigateClick = React.useCallback((menu: MenuType) => {
		if (menu.to !== undefined) {
			navigate(menu.to)
		}
	}, [])

	const onCloseSubmenu = React.useCallback(() => {
		setAnchorEl(null)
		setIsOpenSubmenu(false)
	}, [])

	return (
		<View css={style}>
			<ListItemButton
				id={`${props.menu.id}_button`}
				aria-controls={isOpenSubmenu ? `${props.menu.id}_menu` : undefined}
				aria-haspopup="true"
				aria-expanded={isOpenSubmenu ? 'true' : undefined}
				className={`mainMenuButton ${props.isFirstDepth ? 'first-depth' : ''}`}
				css={[{ color: textColor }, !isOpenDrawer ? { justifyContent: 'center' } : null]}
				onClick={e =>
					props.menu.to !== undefined
						? onNavigateClick(props.menu)
						: onOpenSubmenuClick(e)
				}
			>
				<Icon name={iconName} style={{ ...mainItemIconStyle }} />
				{isOpenDrawer || !props.isFirstDepth ? (
					<Text i18n={props.menu.i18n}>{props.menu['name']}</Text>
				) : null}
			</ListItemButton>

			<DrawerSubMenuList
				menu={props.menu}
				anchorEl={anchorEl}
				isOpenSubmenu={isOpenSubmenu}
				onCloseSubmenu={onCloseSubmenu}
				onNavigateClick={onNavigateClick}
			/>
		</View>
	)
}

const style = css({
	display: 'block',

	'.mainMenuButton': {
		height: '2rem',

		'&.first-depth': {
			height: '2.5rem',
		},
	},
})
